import { Box, Button, Chip, Typography } from '@mui/material'
import type { DateTime } from 'luxon'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { ActivationGroup } from '@/features/activationGroup/types'
import { usePermissions } from '@/features/authorization/contexts/PermissionsContext'
import type { FormattedRow } from '@/features/bessDashboard/types'
import CreateBidsButton from '@/features/bidding/components/cascading/review/CreateBidsButton'
import ReviewBidsTable from '@/features/bidding/components/cascading/review/ReviewBidsTable'
import { CASCADE_MARKETS } from '@/features/bidding/constants'
import type { BidLevelInputSymmetrical } from '@/features/bidding/types/bidLadderInputSymmetrical'
import type { PricingStrategyConfig } from '@/features/bidding/types/forecastPricing'
import type { CascadeMarketId } from '@/features/bidding/types/market'
import { transformBidLevelsToExtendedBidsSymmetrical } from '@/features/bidding/utils/cascading/bidLadder/transformBidLevelsToExtendedBidsSymmetrical'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'

type ReviewBidsContentSymmetricalProps = {
  levels: BidLevelInputSymmetrical[]
  deliveryDay: MarketDate
  activationGroupsMap: Map<string, ActivationGroup>
  pricingConfig?: PricingStrategyConfig
  forecastsData?: FormattedRow[]
  selectedDeliveryDay?: DateTime
  selectedMarket: CascadeMarketId
  onBackClick: () => void
  onSuccess: () => void
}

function ReviewBidsContentSymmetrical({
  levels,
  deliveryDay,
  activationGroupsMap,
  pricingConfig,
  forecastsData,
  selectedDeliveryDay,
  selectedMarket,
  onBackClick,
  onSuccess,
}: Readonly<ReviewBidsContentSymmetricalProps>) {
  const { t } = useTranslation()
  const { permissions } = usePermissions()
  const canMutateBidding = permissions.has('biddingMutation')

  const marketLabel = CASCADE_MARKETS.find((m) => m.id === selectedMarket)?.label ?? selectedMarket

  const { bids, errors } = useMemo(
    () =>
      transformBidLevelsToExtendedBidsSymmetrical({
        levels,
        deliveryDay,
        activationGroupsMap,
        pricingConfig,
        forecastsData,
        selectedDeliveryDay,
      }),
    [levels, deliveryDay, activationGroupsMap, pricingConfig, forecastsData, selectedDeliveryDay],
  )

  // For symmetrical markets, all bids are treated the same (no UP/DOWN distinction)
  const activeBids = bids.filter((bid) => {
    // Filter out zero bids (unassigned groups)
    const hasVolume = bid.ptus.some((ptu) => (ptu.offeredVolume?.quantity ?? 0) > 0)
    return hasVolume
  })

  const hasErrors = errors.length > 0

  // Calculate summary stats
  const totalBids = activeBids.length
  const totalCapacityMW = useMemo(() => {
    return levels.reduce((sum, level) => {
      const levelCapacity = level.assignedGroups.reduce((s, g) => s + g.capacityMW, 0)
      return sum + levelCapacity
    }, 0)
  }, [levels])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: 'background.paper',
      }}
    >
      {/* Header with title, market chip, and delivery day */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 3,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography component="h2" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
          {t('bidding.bidding_cascading.review_bids.title')}
        </Typography>
        <Chip
          label={marketLabel}
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: '0.7rem',
            height: 20,
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
          }}
        />
        <Typography color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          •
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          {deliveryDay.toString()}
        </Typography>
      </Box>

      {/* Summary stats */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          px: 3,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'grey.50',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', textTransform: 'uppercase' }}>
            {t('bidding.bidding_cascading.review_bids.summary.total_bids')}
          </Typography>
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 600 }}>{totalBids}</Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', textTransform: 'uppercase' }}>
            {t('bidding.bidding_cascading.review_bids.summary.total_volume')}
          </Typography>
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 600 }}>{totalCapacityMW.toFixed(1)} MW</Typography>
        </Box>
        {hasErrors && (
          <Box>
            <Typography sx={{ fontSize: '0.6875rem', color: 'error.main', textTransform: 'uppercase' }}>
              Errors
            </Typography>
            <Typography sx={{ fontSize: '1.25rem', fontWeight: 600, color: 'error.main' }}>{errors.length}</Typography>
          </Box>
        )}
      </Box>

      {/* Bids table */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          px: 3,
          py: 2,
        }}
      >
        {activeBids.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'text.secondary',
            }}
          >
            <Typography>{t('bidding.bidding_cascading.review_bids.no_bids')}</Typography>
          </Box>
        ) : (
          <ReviewBidsTable activationGroupsMap={activationGroupsMap} bids={activeBids} errors={errors} />
        )}

        {/* Errors section */}
        {hasErrors && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontWeight: 600, color: 'error.main', mb: 1 }}>Validation Errors</Typography>
            {errors.map((error) => (
              <Box
                key={`${error.levelId}-${error.groupUuid}`}
                sx={{
                  p: 1,
                  mb: 0.5,
                  backgroundColor: 'error.lighter',
                  borderRadius: 1,
                  fontSize: '0.8125rem',
                }}
              >
                <Typography sx={{ fontWeight: 600 }}>{error.groupCode}</Typography>
                <Typography color="error.dark">{error.message}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Bottom Actions */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          justifyContent: 'flex-end',
          alignItems: 'center',
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button variant="outlined" onClick={onBackClick}>
          {t('bidding.bidding_cascading.review_bids.back_to_bid_ladder')}
        </Button>
        {canMutateBidding && <CreateBidsButton bids={bids} disabled={hasErrors} onSuccess={onSuccess} />}
      </Box>
    </Box>
  )
}

export default ReviewBidsContentSymmetrical
